/**
 * A single customer project in an Emerald Warranty claim
 *
 * This class emits events:
 * 'pricechange' - whenever squares are updated
 * 'error' - when validation fails
 * 'incomplete' - when toggleError is called
 */

import moment from "moment";
import AjaxUtils from "../util/ajax-utils";

export default class Project {

    constructor(element, contractorEmail, contractorName, contractorId, uniqueString) {
        this.element = element;

        this.uniqueString = uniqueString ? uniqueString : Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

        this.contractorEmail = contractorEmail;
        this.contractorName = contractorName;
        this.contractorId = contractorId;

        this.isIncomplete = true;

        this.warrantyType = element.querySelector('#warranty-type');
        this.appendUniqueStringToAttribute(this.warrantyType, this.uniqueString, 'id');
        this.priceNote = element.querySelector('#price-note');
        this.appendUniqueStringToAttribute(this.priceNote, this.uniqueString, 'id');
        this.qualifiedProductsNote = element.querySelector('#qualified-products-note');
        this.appendUniqueStringToAttribute(this.qualifiedProductsNote, this.uniqueString, 'id');

        this.projectName = element.querySelector('#project-name');
        this.appendUniqueStringToAttribute(this.projectName, this.uniqueString, 'id');
        this.installationDate = element.querySelector('#installation-date');
        this.appendUniqueStringToAttribute(this.installationDate, this.uniqueString, 'id');
        this.firstName = element.querySelector('#first-name');
        this.appendUniqueStringToAttribute(this.firstName, this.uniqueString, 'id');
        this.lastName = element.querySelector('#last-name');
        this.appendUniqueStringToAttribute(this.lastName, this.uniqueString, 'id');
        this.email = element.querySelector('#homeowner-email');
        this.appendUniqueStringToAttribute(this.email, this.uniqueString, 'id');

        this.isMailingAddressSameCheck = element.querySelector('#mailing-address-same');
        this.appendUniqueStringToAttribute(this.isMailingAddressSameCheck, this.uniqueString, 'id');
        this.projectAddressSection = element.querySelector('.form-emerald-pro__project_address_section');
        this.mailingAddress = element.querySelector('#mailing-address');
        this.appendUniqueStringToAttribute(this.mailingAddress, this.uniqueString, 'id');
        this.mailingCity = element.querySelector('#mailing-city');
        this.appendUniqueStringToAttribute(this.mailingCity, this.uniqueString, 'id');
        this.mailingState = element.querySelector('#mailing-state');
        this.appendUniqueStringToAttribute(this.mailingState, this.uniqueString, 'id');
        this.mailingZip = element.querySelector('#mailing-zip');
        this.appendUniqueStringToAttribute(this.mailingZip, this.uniqueString, 'id');
        this.mailingPhone = element.querySelector('#mailing-phone');
        this.appendUniqueStringToAttribute(this.mailingPhone, this.uniqueString, 'id');
        this.projectAddress = element.querySelector('#project-address');
        this.appendUniqueStringToAttribute(this.projectAddress, this.uniqueString, 'id');
        this.projectCity = element.querySelector('#project-city');
        this.appendUniqueStringToAttribute(this.projectCity, this.uniqueString, 'id');
        this.projectState = element.querySelector('#project-state');
        this.appendUniqueStringToAttribute(this.projectState, this.uniqueString, 'id');
        this.projectZip = element.querySelector('#project-zip');
        this.appendUniqueStringToAttribute(this.projectZip, this.uniqueString, 'id');
        this.projectPhone = element.querySelector('#project-phone');
        this.appendUniqueStringToAttribute(this.projectPhone, this.uniqueString, 'id');

        this.productInstalledSelect = element.querySelector('#product-installed');
        this.appendUniqueStringToAttribute(this.productInstalledSelect, this.uniqueString, 'id');
        this.productColorSelect = element.querySelector('#product-color');
        this.appendUniqueStringToAttribute(this.productColorSelect, this.uniqueString, 'id');
        this.productSquares = element.querySelector('#product-squares');
        this.appendUniqueStringToAttribute(this.productSquares, this.uniqueString, 'id');
        this.productDate = element.querySelector('#product-date');
        this.appendUniqueStringToAttribute(this.productDate, this.uniqueString, 'id');
        this.productCost = element.querySelector('#product-cost');
        this.appendUniqueStringToAttribute(this.productCost, this.uniqueString, 'id');
        this.productOmniseal = element.querySelector('#product-omniseal');
        this.appendUniqueStringToAttribute(this.productOmniseal, this.uniqueString, 'id');
        this.omnisealOptionSection = element.querySelector('.omniseal-option-section');
        this.omnisealProductColor = element.querySelector('#omniseal-color');
        this.appendUniqueStringToAttribute(this.omnisealProductColor, this.uniqueString, 'id');
        this.omnisealSquares = element.querySelector('#omniseal-squares');
        this.appendUniqueStringToAttribute(this.omnisealSquares, this.uniqueString, 'id');
        this.omnisealLowSlope = element.querySelector('#omniseal-low-slope');
        this.appendUniqueStringToAttribute(this.omnisealLowSlope, this.uniqueString, 'id');

        this.smartStart = element.querySelector('#smart-start');
        this.appendUniqueStringToAttribute(this.smartStart, this.uniqueString, 'id');
        this.hipAndRidgeShinglesSelect = element.querySelector('#hip-and-ridge-shingles');
        this.appendUniqueStringToAttribute(this.hipAndRidgeShinglesSelect, this.uniqueString, 'id');
        this.underlaymentSelect = element.querySelector('#underlayments');
        this.appendUniqueStringToAttribute(this.underlaymentSelect, this.uniqueString, 'id');
        this.protectionSelect = element.querySelector('#protection');
        this.appendUniqueStringToAttribute(this.protectionSelect, this.uniqueString, 'id');

        this.enhancedWindWarrantyCheck = element.querySelector('#enhanced-wind-warranty');
        this.appendUniqueStringToAttribute(this.enhancedWindWarrantyCheck, this.uniqueString, 'id');

        element.querySelectorAll('label[for]').forEach(el => {
            if (el) { this.appendUniqueStringToAttribute(el, this.uniqueString, 'for'); }
        });

        this.filePicker = element.querySelector('#proof-files');
        this.appendUniqueStringToAttribute(this.filePicker, this.uniqueString, 'id');
        this.fileDropContainer = element.querySelector('.proof-of-purchase');
        this.dropZone = element.querySelector('.file-drop');
        this.dropZoneMesagge = element.querySelector('.dz-message');
        this.uploadButton = element.querySelector('.upload-button');
        this.uploadConfirmContainer = element.querySelector('.confirm-container');
        this.files = new Set();
        this.totalFilesSize = 0;
        this.totalFiles = 0;

        this.projectHeader = element.querySelector('.form-emerald-pro__project__header');
        this.expandButton = element.querySelector('.form-emerald-pro__project__header .toggle-button');
        this.projectView = element.querySelector('.form-emerald-pro__project__content');
        this.projectHeaderTitle = element.querySelector('.form-emerald-pro__project__header .title');
        this.projectHeaderDate = element.querySelector('.form-emerald-pro__project__header .date');

        this.saveProgressButton = element.querySelector('#save-progress-button');
        this.deleteProjectButton = element.querySelector('#delete-project-button');

        this.saveMessage = element.querySelector('.save-message');

        this.registerListeners();
    }

    appendUniqueStringToAttribute(element, uniqueString, attribute) {
        element.setAttribute(attribute, element.getAttribute(attribute) + '-' + uniqueString);
    }

    showSaveMessage() {
        const fadeOut = () => {
            this.saveMessage.removeEventListener('transitionend', fadeOut);
            this.saveMessage.style.opacity = 0;
        }
        this.saveMessage.addEventListener('transitionend', fadeOut);
        this.saveMessage.style.opacity = 1;
    }

    addEventListener(event, callback) {
        this.element.addEventListener(event, callback);
    }

    dispatchEvent(event, detail) {
        const e = detail ? new CustomEvent(event, {
            detail: detail
        }) : new Event(event);
        this.element.dispatchEvent(e);
    }

    getCostPerSquare() {
        switch(this.warrantyType.value) {
            case 'emerald_pro':
                return 4.0;
            case 'emerald_premium':
                return 8.0;
            default:
                return 0.0;
        }
    }

    getTotalCost() {
        let squares = Number(this.productSquares.value) + Number(this.omnisealSquares.value);
        return squares * this.getCostPerSquare();
    }

    detachFromParent() {
        this.element.parentNode.removeChild(this.element);
    }

    collapse() {
        this.projectView.classList.add('form-emerald-pro__project__content__collapsed');
        this.projectHeader.classList.remove('selected');
        this.expandButton.querySelector('.inner-text').innerText = "Edit Project";
        this.expandButton.querySelector('.carat-section').classList.remove('expanded');
    }

    expand() {
        this.projectView.classList.remove('form-emerald-pro__project__content__collapsed');
        this.projectHeader.classList.add('selected');
        this.expandButton.querySelector('.inner-text').innerText = "Collapse Project";
        this.expandButton.querySelector('.carat-section').classList.add('expanded');
    }

    getPaymentNoteElement() {
        let p = document.createElement('p');
        p.classList.add('note');
        p.classList.add('p4');
        p.classList.add('black');

        switch (this.warrantyType.value) {
            case 'emerald_pro':
                p.innerHTML = '<strong>Note:</strong> To complete your Emerald Pro Warranty Registration, you will need to submit full payment as indicated on your rate calculator—$4.00 per square equals total cost (Shingles + OmniSeal™) x $4.00. Please enter your credit card information below to finalize your registration. Once submitted, you will receive an email confirmation displaying your warranty information and proof of purchase.';
                break;
            case 'emerald_premium':
                p.innerHTML = '<strong>Note:</strong> To complete your Emerald Premium Warranty Registration, you will need to submit full payment as indicated on your rate calculator—$8.00 per square equals total cost (Shingles + OmniSeal™) x $8.00. Please enter your credit card information below to finalize your registration. Once submitted, you will receive an email confirmation displaying your warranty information and proof of purchase.';
                break;
        }

        return p;
    }

    serialize() {
        const fields = this.rootElement.querySelectorAll('input:not([type="file"]), select');

        let data = {};
        data.uniqueString = this.uniqueString;
        data.elements = [];

        fields.forEach((field) => {
            data.elements.push({
                id: field.id,
                value: field.value,
                checked: field.checked ?? false
            });
        });

        return JSON.stringify(data);
    }

    static fromJson(json, root, contractorEmail, contractorName, contractorId) {
        const project = new Project(root, contractorEmail, contractorName, contractorId, json.uniqueString);
        json.elements.forEach(v => {
            if(v.id) {
                project.rootElement.querySelector(`#${v.id}`).value = v.value;
                project.rootElement.querySelector(`#${v.id}`).checked = v.checked
            }
        });
        project.handleProjectNameChange(project);
        project.dispatchEvent(new Event('pricechange'));
        return project;
    }

    get rootElement() {
        return this.element;
    }

    static validFileTypes() {
        let validMap = new Map();
        validMap.set("image/png", true);
        validMap.set("image/jpg", true);
        validMap.set("image/jpeg", true);
        validMap.set("application/pdf", true);
        validMap.set("image/gif", true);
        return validMap;
    }

    handleProjectDateChange() {
        if(window.validator.isDate(this.installationDate.value)) {
            const date = moment(this.installationDate.value);
            this.projectHeaderDate.style.display = 'block';
            this.projectHeaderDate.innerText = `Installation Date: ${date.format('MMMM D, Y')}`;
        } else {
            this.projectHeaderDate.innerText = "";
            this.projectHeaderDate.style.display = 'none';
        }
    }

    registerListeners() {
        const _this = this;

        /*
         * File drop
         */
        window.addEventListener('dragover', e => e.preventDefault());
        window.addEventListener('drop', e => e.preventDefault());
        this.dropZone.addEventListener('drop', e => _this.handleDrop(e, _this));
        this.dropZone.addEventListener('dragenter', e => _this.handleDragOver(e, _this));
        this.dropZone.addEventListener('dragleave', e => _this.handleDragExit(e, _this));
        this.dropZone.addEventListener('dragend', e => e.preventDefault());
        this.uploadButton.addEventListener('click', e => {
            e.preventDefault();
            this.filePicker.click();
        })
        this.filePicker.addEventListener('change', e => _this.handleFileReceived(e.target.files ?? null, _this));

        /*
        Project Name
         */
        this.projectName.addEventListener('input', () => _this.handleProjectNameChange(_this));
        this.firstName.addEventListener('input', () => _this.handleProjectNameChange(_this));
        this.lastName.addEventListener('input', () => _this.handleProjectNameChange(_this));
        this.expandButton.addEventListener('click', () => {
            if(_this.projectHeader.classList.contains('selected')) {
                _this.collapse();
            } else {
                _this.expand();
            }
        });
        this.installationDate.addEventListener('change', () => {
            this.handleProjectDateChange();
        });

        /*
        Reset Error states
         */
        this.element.querySelectorAll('.text-input input').forEach(node => {
            node.addEventListener('input', _this.resetErrorState);
        });
        this.element.querySelectorAll('.text-input select').forEach(node => {
            node.addEventListener('input', _this.resetErrorState);
        });

        /*
        Saving/Loading/Deleting
         */
        this.saveProgressButton.addEventListener('click', (e) => {
            e.preventDefault();
            _this.performValidation();
            _this.element.dispatchEvent(new CustomEvent('save', {
                detail: this
            }));
        });
        this.deleteProjectButton.addEventListener('click', e => {
            e.preventDefault();
            const event = new CustomEvent('delete', {
                detail: {
                    project: _this
                }
            });
            _this.element.dispatchEvent(event);
        });

        /*
        Price Changes
         */
        this.warrantyType.addEventListener('change', (e) => {
            this.element.setAttribute('data-warranty-type', e.target.value);
            this.toggleError(e.target, e.target.value.length === 0, null, true);
            this.handlePriceChange();
            this.priceNote.innerHTML = '';
            this.priceNote.appendChild(this.getPaymentNoteElement());
            if(e.target.value === 'emerald_premium') {
                this.productSquares.setAttribute('max', 199);
            } else {
                this.productSquares.removeAttribute('max');
            }

        });
        this.productSquares.addEventListener('input', () => {
            this.handlePriceChange();
        });
        this.omnisealSquares.addEventListener('input', () => {
            this.handlePriceChange();
        });

        /*
        Enhanced Wind Warranty
         */
        this.smartStart.addEventListener('change', e => {
            this.starterShingleSecureChoice = e.target.checked;
            this.toggleSecureChoice();
            this.toggleError(e.target.closest('.check-wrapper'), !this.hasSmartStart(), null, true);
        });
        this.hipAndRidgeShinglesSelect.addEventListener('change', e => {
            const secureChoice = e.target.options[e.target.options.selectedIndex].dataset.secureChoice;
            this.hipAndRidgeSecureChoice = Boolean(secureChoice);
            this.toggleSecureChoice();
        });
        this.underlaymentSelect.addEventListener('change', e => {
            const secureChoice = e.target.options[e.target.options.selectedIndex].dataset.secureChoice;
            this.underlaymentSecureChoice = Boolean(secureChoice);
            this.toggleSecureChoice();
        });
        this.protectionSelect.addEventListener('change', () => {
            this.toggleSecureChoice();
        })

        /*
        Optional form sections
         */
        this.isMailingAddressSameCheck.addEventListener('change', e => {
            if(e.target.checked) {
                this.projectAddressSection.style.display = 'none';
            } else {
                this.projectAddressSection.style.display = 'block';
            }
        });
        this.productOmniseal.addEventListener('change', e => {
            if(e.target.checked) {
                this.omnisealOptionSection.style.display = 'block';
            } else {
                this.omnisealOptionSection.style.display = 'none';
            }
        });

        /*
        Input validation on input change
         */
        this.element.querySelectorAll('.text-input input[required]').forEach(node => {
            node.addEventListener('input', () => {
                this.validateTextInput(node, true);
            });
            node.addEventListener('change', () => {
                this.validateTextInput(node, true);
            });
        });
        this.element.querySelectorAll('.text-input select').forEach(node => {
            node.addEventListener('change', () => {
                this.validateSelect(node, true);
            });
        });

        /*
        Observe scroll to determine if save/delete buttons are visible
         */
        window.addEventListener('scroll', () => {
            const buttonContainer = this.element.querySelector('.save-delete-container');
            const buttonRect = buttonContainer.getBoundingClientRect();
            const formRect = this.element.getBoundingClientRect();

            if((buttonRect.bottom + 51) < formRect.bottom) {
                buttonContainer.classList.add('moving');
            } else {
                buttonContainer.classList.remove('moving');
            }
        });

        /*
        Listen to each input to change Project Incomplete state.
         */
        this.element.querySelectorAll('.text-input input[required], .text-input select, .check-wrapper input' ).forEach(node => {
            node.addEventListener('input', () => {
                this.performValidation(true, true);
            });
        });
    }

    resetErrorState(e) {
        e.target.closest('.text-input').classList.remove('error');
    }

    static isVisible(node) {
        return node.clientHeight !== 0 && node.getClientRects().length !== 0 && window.getComputedStyle(node).display !== 'none';
    }

    performValidation(skipScroll = false, silent = false, shouldCollapse = true) {
        let isValid = true;
        if (!silent) {
            this.expand();
        }

        this.toggleSecureChoice();

        try {
            this.element.querySelectorAll('.text-input input[required]').forEach(node => {
                if(Project.isVisible(node)) {
                    isValid &= this.validateTextInput(node, skipScroll, silent);
                    if(!isValid) throw false;
                }
            });

            this.element.querySelectorAll('.text-input select[required]').forEach(node => {
                if(Project.isVisible(node)) {
                    isValid &= this.validateSelect(node, skipScroll, silent);
                    if(!isValid) throw false;
                }
            });

            this.element.querySelectorAll('.check-wrapper input[required]').forEach(node => {
                if(Project.isVisible(node)) {
                    isValid &= this.validateCheck(node, skipScroll, silent);
                    if(!isValid) throw false;
                }
            });

            isValid &= this.validateSquareCount(skipScroll, silent);
            if(!isValid) throw false;

            isValid &= this.validateAccessories(skipScroll, silent);
            if(!isValid) throw false;

            if(this.files.size === 0) {
                this.toggleError(this.fileDropContainer, true, "Proof of purchase is required", skipScroll, silent);
                isValid = false;
            } else {
                isValid &= this.updateFileUploadError(skipScroll, silent);
            }
            if(!isValid) throw false;

        } catch (e) {
            isValid = e;
        }

        if (!silent && shouldCollapse) {
            this.collapse();
        }

        this.setIncomplete(!isValid);
        this.dispatchEvent('incomplete', {
            isIncomplete: !isValid,
        });

        return isValid;
    }

    /**
     * Updates the total file size tally for all forms
     * @param change - use positive to add to the count, use negative to subtract from the count
     */
    updateFileUploadSizeCount(change) {
        let totalUploadSize = parseInt(document.querySelector('#form-emerald-pro').getAttribute('data-upload-size'));
        totalUploadSize += change;
        document.querySelector('#form-emerald-pro').setAttribute('data-upload-size', totalUploadSize.toString());
    }

    updateFileUploadError(skipScroll = false, silent = false) {
        let isValid;
        let totalUploadSize = parseInt(document.querySelector('#form-emerald-pro').getAttribute('data-upload-size'));
        if (totalUploadSize >= AjaxUtils.MAX_UPLOAD_SIZE) {
            this.toggleError(this.fileDropContainer, true, `Total file upload for all warranties must not exceed ${AjaxUtils.MAX_UPLOAD_SIZE_MB} MB. Current upload: ${(totalUploadSize/1048576).toFixed(1)} MB`, skipScroll, silent);
            isValid = false;
         } else if(this.files.size === 0) {
            this.toggleError(this.fileDropContainer, true, 'Proof of purchase is required.', skipScroll, silent);
            isValid = false;
        } else {
            this.toggleError(this.fileDropContainer, false, null, skipScroll, silent);
            isValid = true;
        }

        this.dispatchEvent('incomplete', {
            isIncomplete: !isValid,
        });

        return isValid;
    }

    clearAllErrors() {
        this.element.querySelectorAll('.text-input input, .text-input select').forEach(node => {
            this.toggleError(node, false);
        });
    }

    getAdjustedAccessoryCount(select) {
        if(select.value.length === 0) {
            return 0;
        }

        const selectedIndex = select.selectedIndex;
        const option = select.options[selectedIndex];
        const countsToward = option.dataset.countsTowards;

        if(!countsToward) {
            return 1;
        }

        return this.warrantyType.value === countsToward || countsToward === 'everyone' ? 1 : 0;
    }

    validateSquareCount(skipScroll = true, silent = false) {
        const count = this.productSquares.value ? Number(this.productSquares.value) : 0;
        if(this.warrantyType.value === 'emerald_premium' && count >= 200) {
            this.toggleError(this.productSquares, true, "Registering Emerald Premium projects of 200 squares or more? <a href='https://www.malarkeyroofing.com/app/uploads/2021/05/emerald-premium-200-square-warranty-form-malarkey.pdf' target='_blank'>Use this form instead.</a>", skipScroll, silent);
            return false;
        }

        this.toggleError(this.productSquares, this.productSquares.val === 0, null, skipScroll, silent);
        return true;
    }

    validateAccessories(skipScroll = false, silent = false) {
        let accessoryCount = 0;
        accessoryCount += this.hasSmartStart() ? 1 : 0;
        accessoryCount += this.getAdjustedAccessoryCount(this.hipAndRidgeShinglesSelect);
        accessoryCount += this.getAdjustedAccessoryCount(this.underlaymentSelect);
        accessoryCount += this.getAdjustedAccessoryCount(this.protectionSelect);

        let valid;

        switch(this.warrantyType.value) {
            case 'emerald_pro':
                valid = accessoryCount >= 3;
                break;
            case 'emerald_premium':
                valid = accessoryCount >= 4;
                break;
            default:
                valid = false;
        }

        this.toggleError(this.qualifiedProductsNote, !valid, null, skipScroll, silent);

        return valid;
    }

    validateSelect(node, skipScroll = false, silent = false) {
        if(node.value.length === 0) {
            this.toggleError(node, true, null, skipScroll, silent);
            return false;
        } else {
            this.toggleError(node, false, silent);
            return true;
        }

    }

    validateCheck(node, skipScroll = false, silent=false) {
        if(!node.checked) {
            this.toggleError(node.closest('.check-wrapper'), true, null, skipScroll, silent);
            return false;
        }

        return true;
    }

    validateTextInput(node, skipScroll = false, silent = false) {
        const isDateInvalid = !window.validator.isDate(node.value) || window.validator.isAfter(node.value);

        switch(node.type) {
            case 'email':
                if(node.value.length === 0 || !window.validator.isEmail(node.value)) {
                    this.toggleError(node, true, null, skipScroll, silent);
                    return false;
                }
                break;
            case 'date':
                if(node.value.length === 0 || isDateInvalid) {
                    this.toggleError(node, true, null, skipScroll, silent);
                    return false;
                }
                break;
            case 'number':
                if(node.value.length === 0 || !window.validator.isNumeric(node.value) || Number(node.value) < 1) {
                    this.toggleError(node, true, null, skipScroll, silent);
                    return false;
                }
                break;
            case 'tel':
                if(node.value.length === 0 || !window.validator.isMobilePhone(node.value)) {
                    this.toggleError(node, true, null, skipScroll, silent);
                    return false;
                }
                break;
            case 'text':
            default:
                if(node.value.length === 0 || (node.classList.contains('date') && isDateInvalid)) {
                    this.toggleError(node, true, null, skipScroll, silent);
                    return false;
                }
                break;
        }

        return true;
    }

    toggleSecureChoice() {
        this.enhancedWindWarrantyCheck.checked = this.validateAccessories(true, true);
    }

    handlePriceChange() {
        const cost = this.getTotalCost();
        this.dispatchEvent('pricechange', {
            name: this.projectName.value,
            cost: cost
        });
    }

    getName() {
        return this.projectHeaderTitle.innerText;
    }

    toggleError(element, showError, message, skipEvent = false, silent = false) {

        if (showError && silent) {
            return;
        }

        let closest = element.closest('.text-input') ?? element;
        if(showError) {
            closest.classList.add('error');
            this.projectHeader.classList.add('error');

            if(message) {
                closest.querySelector('.error-message').innerHTML = message;
            }

            if(!skipEvent) {
                this.dispatchEvent('error', {
                    element: element,
                    header: this.projectHeader
                });
            }
        } else {
            if (!document.querySelectorAll(".text-input.error").length) {
              this.projectHeader.classList.remove('error');
            }
            closest.classList.remove('error');
            this.dispatchEvent('clearerror');
        }

    }

    setIncomplete (isIncomplete) {
        this.isIncomplete = isIncomplete;
    }

    handleFileReceived(files) {
        // files is a FileList of File objects.
        this.toggleError(this.fileDropContainer, false);
        this.totalFiles += files.length;
        this.handleMaxFiles(this.totalFiles);

        if (this.totalFiles <= 5) {
            Array.from(files).forEach((file) => {
                const isValid = Project.validFileTypes().get(file.type);
                this.totalFilesSize += file.size;
                this.updateFileUploadSizeCount(file.size);

                if(isValid) {
                    this.toggleError(this.fileDropContainer, false);

                    if(this.files.has(file)) {
                        this.handleDeleteFile(file);
                    }

                    this.files.add(file);
                    this.renderFileConfirm();

                } else {
                    console.error("Invalid file");
                    this.toggleError(this.fileDropContainer, true, "Invalid file", true);
                }
            });
            this.filePicker.value = '';
        } else {
            this.totalFiles -= files.length;
            this.handleMaxFiles(this.totalFiles);
            this.toggleError(this.fileDropContainer, true, "A maximum of 5 files may be uploaded per project", true);
        }
        this.performValidation(true, true);
        this.updateFileUploadError(true, false);
        this.dispatchEvent('filesupdated');
    }

    getConfirmLine(file, fileCount) {
        const node = document.createElement('div');
        node.classList.add('upload-confirm');
        node.classList.add('visible');
        node.innerHTML = `<p class="filename p3 bold">${file.name}</p>
                        <button class="trash-button">
                            <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M13.1005 15H3.87607C3.11641 15 2.51953 14.4031 2.51953 13.6435V3.3338H14.4571V13.6435C14.4571 14.4031 13.8602 15 13.1005 15Z" stroke="#D28122" stroke-miterlimit="10"/>
                                <path d="M5.61133 7.13141V11.201" stroke="#D28122" stroke-miterlimit="10" stroke-linecap="round"/>
                                <path d="M11.3086 7.13141V11.201" stroke="#D28122" stroke-miterlimit="10" stroke-linecap="round"/>
                                <path d="M8.48828 6.15521V12.124" stroke="#D28122" stroke-miterlimit="10" stroke-linecap="round"/>
                                <path d="M1 3.3338H15.9219" stroke="#D28122" stroke-miterlimit="10" stroke-linecap="round"/>
                                <path d="M10.3887 1H6.59041C6.0478 1 5.66797 1.43409 5.66797 1.92245V3.27898H11.3112V1.92245C11.3112 1.43409 10.9313 1 10.3887 1Z" stroke="#D28122" stroke-miterlimit="10" stroke-linecap="round"/>
                            </svg>
                        </button>
                        <span class="file-counter">${fileCount}/5</span>`;

        node.querySelector('button.trash-button').addEventListener('click', () => {
            this.handleDeleteFile(file);
        });
        return node;
    }

    handleDeleteFile(file) {
        this.files.delete(file);
        this.totalFilesSize -= file.size;
        this.totalFiles--;
        this.handleMaxFiles(this.totalFiles);
        this.renderFileConfirm();
        this.updateFileUploadSizeCount(-file.size);
        this.performValidation(true, true);
        this.dispatchEvent('filesupdated');
    }

    renderFileConfirm() {
        this.uploadConfirmContainer.innerHTML = "";
        let fileCount = 0;
        // files is a Set and forEach doesn't provide an index arg for Sets.
        this.files.forEach(file => {
            fileCount++;
            if (fileCount <= 5) {
                this.uploadConfirmContainer.appendChild(this.getConfirmLine(file, fileCount));
            }
        });
    }

    handleMaxFiles(numFiles) {
        if(numFiles >= 5) {
            this.dropZone.classList.add('disabled');
            this.dropZoneMesagge.classList.add('hidden');
        } else {
            this.dropZone.classList.remove('disabled');
            this.dropZoneMesagge.classList.remove('hidden');
        }
    }

    handleDrop(e, _this) {
        _this.handleDragExit(e, _this);

        if (e.dataTransfer.items) {
            if (e.dataTransfer.items.length > 0 && e.dataTransfer.items.length <= 5 && e.dataTransfer.items[0].kind === 'file') {
                let files = [];
                for(const item of e.dataTransfer.items) {
                    if(item.kind === 'file') {
                        files.push(item.getAsFile());
                    }
                }
                _this.handleFileReceived(files, _this);
            }
        } else if(e.dataTransfer.files.length > 0 && e.dataTransfer.items.length <= 5) {
            _this.handleFileReceived(e.dataTransfer.files, _this);
        } else {
            this.toggleError(this.fileDropContainer, true, "You may only upload 5 files per project.", true);
        }
    }

    handleDragOver(e, _this) {
        e.preventDefault();
        _this.dropZone.classList.add('form-emerald-pro__dropzone__over');
    }

    handleDragExit(e, _this) {
        e.preventDefault();
        _this.dropZone.classList.remove('form-emerald-pro__dropzone__over');
    }

    handleProjectNameChange(_this) {
        let name = _this.projectName.value && _this.projectName.value !== "" ? _this.projectName.value : `${_this.firstName.value} ${_this.lastName.value}`;
        if(name === ' ') {
            name = "Untitled Project"
        }

        _this.projectHeaderTitle.innerText = name;
        _this.projectHeaderTitle.id = `tag-project-${name.replace(' ', '-')}`;

        _this.handlePriceChange();
    }

    hasSmartStart() {
        return this.smartStart.checked;
    }

    getCompiledProjectData() {
        let project = {
            contractorEmail: this.contractorEmail,
            contractorName: this.contractorName,
            contractorId: this.contractorId,
            warrantyType: this.warrantyType.value === 'emerald_premium' ? 'Premium' : 'Pro',
            installationDate: this.installationDate.value,
            firstName: this.firstName.value,
            lastName: this.lastName.value,
            phone: this.mailingPhone.value,
            projectEmail: this.email.value,
            projectName: this.projectHeaderTitle.innerText,
            mailingAddress: this.mailingAddress.value,
            mailingCity: this.mailingCity.value,
            mailingState: this.mailingState.value,
            mailingZip: this.mailingZip.value,
            projectAddress: !this.isMailingAddressSameCheck.checked ? this.projectAddress.value : this.mailingAddress.value,
            projectCity: !this.isMailingAddressSameCheck.checked ? this.projectCity.value : this.mailingCity.value,
            projectState: !this.isMailingAddressSameCheck.checked ? this.projectState.value : this.mailingState.value,
            projectZip: !this.isMailingAddressSameCheck.checked ? this.projectZip.value : this.mailingZip.value,
            shingleInstalled: this.productInstalledSelect.value,
            shingleColor: this.productColorSelect.value,
            shingleSquares: this.productSquares.value,
            runDateOnBundle: this.productDate.value.length > 0 ? this.productDate.value : '',
            costOfInstallation: this.productCost.value,
            omnisealColor: this.omnisealProductColor.value,
            omnisealSquares: this.omnisealSquares.value,
            omnisealSystem: this.omnisealLowSlope.value,
            starterShingles: 'Smart Start',
            hipAndRidgeShingles: this.hipAndRidgeShinglesSelect.value,
            underlayments: this.underlaymentSelect.value,
            iceAndWaterProtection: this.protectionSelect.value,
            enhancedWindWarranty: this.enhancedWindWarrantyCheck.checked,
            smartStart: this.hasSmartStart(),
            proofOfPurchaseFiles: Array.from(this.files),
            totalCost: this.getTotalCost()
        };

        return project;
    }
}
